import { useEffect, useState } from "react";
import { supabase } from "../../../supabaseClient";

const normalizeEmail = (value) => (value || "").trim().toLowerCase();

const abbreviatePositionLabel = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const segmented = normalized.split(/[\/,\n]+/).map((part) => part.trim()).filter(Boolean);
  if (segmented.length > 1) {
    return segmented.map((segment) => abbreviatePositionLabel(segment)).join("/");
  }

  if (normalized.length <= 4 && normalized === normalized.toUpperCase()) return normalized;

  const parts = normalized.split(/[\s-]+/).filter(Boolean);
  if (parts.length > 1) {
    const allShortUpper = parts.every(
      (part) => part.length <= 3 && part === part.toUpperCase()
    );
    if (allShortUpper) return parts.join("/");
    return parts.map((part) => part.charAt(0).toUpperCase()).join("");
  }

  return normalized.slice(0, 3).toUpperCase();
};

const clampReadinessPercent = (userScore, targetScore) => {
  const user = Number(userScore);
  const target = Number(targetScore);
  if (!Number.isFinite(user) || !Number.isFinite(target) || target <= 0) return 0;
  return Math.max(0, Math.min((user / target) * 100, 100));
};

const getAthletePlayerTypeKey = (athlete) => String(athlete?.user_id || athlete?.id || "");

const fetchAllPositionsBySport = async (sportId) => {
  if (!sportId) return [];
  const pageSize = 100;
  let from = 0;
  let all = [];
  let total = null;
  while (true) {
    const { data, error, count } = await supabase
      .from("positions")
      .select("id, name, abbreviation", { count: "exact" })
      .eq("sport_id", sportId)
      .order("name", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = data || [];
    all = all.concat(rows);
    if (typeof count === "number") total = count;
    if (total !== null) {
      if (all.length >= total) break;
      from += rows.length;
      if (rows.length === 0) break;
      continue;
    }
    if (rows.length === 0) break;
    from += rows.length;
  }
  return all;
};

export const useAdminDashboardData = ({
  selectedProgramId,
  selectedTeamId,
  currentProgramSport,
  refreshKey
}) => {
  const [athletes, setAthletes] = useState([]);
  const [draftEvaluations, setDraftEvaluations] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [programSportId, setProgramSportId] = useState("");
  const [playerTypeOptions, setPlayerTypeOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);
  const [playerTypeByAthlete, setPlayerTypeByAthlete] = useState({});
  const [secondaryPlayerTypeByAthletePosition, setSecondaryPlayerTypeByAthletePosition] = useState({});
  const [athletePositionsByAthlete, setAthletePositionsByAthlete] = useState({});
  const [athletePositionIdsByAthlete, setAthletePositionIdsByAthlete] = useState({});
  const [playerTypeDraftByAthlete, setPlayerTypeDraftByAthlete] = useState({});
  const [swotStatusByAthlete, setSwotStatusByAthlete] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchPlayerTypes = async () => {
      const programSport = String(currentProgramSport || "").trim();
      setProgramSportId("");
      setPlayerTypeOptions([]);
      if (!programSport) return;
      try {
        const { data: sportRow, error: sportError } = await supabase
          .from("development_sports")
          .select("id")
          .eq("name", programSport)
          .maybeSingle();
        if (sportError) throw sportError;
        if (!sportRow?.id || cancelled) return;
        setProgramSportId(sportRow.id);

        const { data: typeRows, error: typeError } = await supabase
          .from("player_types")
          .select("id, name")
          .eq("sport_id", sportRow.id)
          .order("name", { ascending: true });
        if (typeError) throw typeError;
        if (!cancelled) setPlayerTypeOptions(typeRows || []);
      } catch (err) {
        if (!cancelled) console.error("Error fetching player types:", err);
      }
    };
    fetchPlayerTypes();
    return () => {
      cancelled = true;
    };
  }, [currentProgramSport]);

  useEffect(() => {
    let cancelled = false;
    const fetchPositions = async () => {
      if (!programSportId) {
        setPositionOptions([]);
        return;
      }
      try {
        console.log("[AdminDashboard] fetchPositions:start", { programSportId });
        const rows = await fetchAllPositionsBySport(programSportId);
        console.log("[AdminDashboard] fetchPositions:done", {
          programSportId,
          count: rows?.length || 0,
          sample: (rows || []).slice(0, 10).map((row) => ({
            id: row.id,
            name: row.name,
            abbreviation: row.abbreviation
          }))
        });
        if (!cancelled) setPositionOptions(rows || []);
      } catch (err) {
        if (!cancelled) console.error("Error fetching positions:", err);
      }
    };
    fetchPositions();
    return () => {
      cancelled = true;
    };
  }, [programSportId]);

  useEffect(() => {
    if (!selectedProgramId) {
      setCoaches([]);
      setAthletes([]);
      setDraftEvaluations([]);
      setSwotStatusByAthlete({});
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        console.log("[AdminDashboard] fetchData start", { selectedProgramId, selectedTeamId });
        const activeProgramId = selectedProgramId;
        const activeTeamId = selectedTeamId;

        const { data: teamRows, error: teamRowsError } = await supabase
          .from("teams")
          .select("id, name")
          .eq("program_id", activeProgramId);
        if (teamRowsError) throw teamRowsError;
        const teamNameById = new Map((teamRows || []).map((t) => [String(t.id), t.name]));

        let coachesQuery = supabase.from("program_coaches")
          .select("id, role, status, team_id, users (full_name, email), teams (name)")
          .eq("program_id", activeProgramId);

        if (activeTeamId) coachesQuery = coachesQuery.eq("team_id", activeTeamId);

        let coachInvitesQuery = supabase.from("coach_invites")
          .select("*")
          .eq("program_id", activeProgramId)
          .is("accepted", null)
          .neq("status", "accepted");

        if (activeTeamId) coachInvitesQuery = coachInvitesQuery.eq("team_id", activeTeamId);

        let playerInvitesQuery = supabase.from("player_invites")
          .select("*")
          .eq("program_id", activeProgramId)
          .eq("status", "pending");

        if (activeTeamId) playerInvitesQuery = playerInvitesQuery.eq("team_id", activeTeamId);

        let rosterQuery = supabase
          .from("rostered_users")
          .select("id, user_id, team_id, teamsnap_name, teamsnap_email, users(full_name, email), teams!inner(program_id)");
        if (activeTeamId) {
          rosterQuery = rosterQuery.eq("team_id", activeTeamId);
        } else {
          rosterQuery = rosterQuery.eq("teams.program_id", activeProgramId);
        }

        const [activeCoachesRes, pendingCoachInvitesRes, pendingPlayerInvitesRes, rosteredRes] = await Promise.all([
          coachesQuery,
          coachInvitesQuery,
          playerInvitesQuery,
          rosterQuery
        ]);

        if (cancelled) return;
        if (activeCoachesRes.error) throw activeCoachesRes.error;
        if (pendingCoachInvitesRes.error) throw pendingCoachInvitesRes.error;
        if (pendingPlayerInvitesRes.error) throw pendingPlayerInvitesRes.error;
        if (rosteredRes.error) throw rosteredRes.error;

        const activeCoaches = activeCoachesRes.data || [];
        const pendingCoachInvites = pendingCoachInvitesRes.data || [];
        const pendingPlayerInvites = pendingPlayerInvitesRes.data || [];
        const rosteredData = rosteredRes.data || [];
        const draftSwots = [];

        const formattedActiveCoaches = activeCoaches.map((c) => ({
          id: c.id,
          name: c.users?.full_name || "Unknown",
          email: c.users?.email,
          role: c.role,
          teamName: c.teams?.name || "Whole Program",
          status: "Active",
          type: "active"
        }));

        const formattedPendingCoaches = pendingCoachInvites.map((i) => ({
          id: i.id,
          name: i.email,
          email: i.email,
          role: i.role,
          teamName: teamNameById.get(String(i.team_id)) || "Whole Program",
          status: "Pending",
          type: "pending"
        }));

        setCoaches([...formattedPendingCoaches, ...formattedActiveCoaches]);

        const pendingInviteByEmail = new Map();
        (pendingPlayerInvites || []).forEach((invite) => {
          const emailKey = normalizeEmail(invite.email);
          if (!emailKey) return;
          const inviteTeamId = String(invite.team_id || "");
          const existing = pendingInviteByEmail.get(emailKey);
          if (!existing) {
            pendingInviteByEmail.set(emailKey, invite);
            return;
          }
          const existingTeamId = String(existing.team_id || "");
          if (activeTeamId && inviteTeamId === String(activeTeamId || "")) {
            pendingInviteByEmail.set(emailKey, invite);
          } else if (!existingTeamId && inviteTeamId) {
            pendingInviteByEmail.set(emailKey, invite);
          }
        });

        const map = new Map();
        rosteredData.forEach((u) => {
          const fallbackName = u.users?.full_name || u.teamsnap_name || "Unnamed Athlete";
          const fallbackEmail = u.users?.email || u.teamsnap_email || "";
          const normalizedFallbackEmail = normalizeEmail(fallbackEmail);
          const isClaimed = Boolean(u.user_id);
          const hasPendingInvite = !isClaimed && normalizedFallbackEmail && pendingInviteByEmail.has(normalizedFallbackEmail);
          const mapKey = u.user_id || `rostered-${u.id}`;
          map.set(mapKey, {
            ...u,
            type: "rostered",
            status: "Active",
            importStatus: isClaimed ? "claimed" : hasPendingInvite ? "invited" : "imported",
            users: {
              ...(u.users || {}),
              full_name: fallbackName,
              email: fallbackEmail
            }
          });
        });

        const isBlankDraft = (draft) => {
          const parseField = (value) => {
            if (Array.isArray(value)) return value;
            if (typeof value === "string") {
              try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
              } catch (err) {
                return [];
              }
            }
            return [];
          };
          return (
            parseField(draft?.strengths).length === 0 &&
            parseField(draft?.weaknesses).length === 0 &&
            parseField(draft?.opportunities).length === 0 &&
            parseField(draft?.threats).length === 0
          );
        };

        const draftByEmail = new Map();
        (draftSwots || []).forEach((draft) => {
          const key = normalizeEmail(draft.prebuilt_email);
          const existing = draftByEmail.get(key);
          if (!key) return;
          const getDraftPriority = (row) => ({
            published: row?.status === "published" ? 1 : 0,
            seeded: isBlankDraft(row) ? 0 : 1,
            createdAt: new Date(row?.created_at || 0).getTime()
          });
          const currentPriority = existing ? getDraftPriority(existing) : null;
          const nextPriority = getDraftPriority(draft);
          const shouldReplace = !existing ||
            nextPriority.published > currentPriority.published ||
            (nextPriority.published === currentPriority.published && nextPriority.seeded > currentPriority.seeded) ||
            (
              nextPriority.published === currentPriority.published &&
              nextPriority.seeded === currentPriority.seeded &&
              nextPriority.createdAt > currentPriority.createdAt
            );
          if (shouldReplace) {
            draftByEmail.set(key, draft);
          }
        });

        const rosteredAthleteIds = rosteredData.map((u) => u.user_id).filter(Boolean);
        const publishedSwotAthleteIds = new Set();
        const swotStatusMap = {};

        const mergedUsers = Array.from(map.values());
        const rosteredEmailsByAthleteId = new Map();
        const rosteredAthleteIdByEmail = new Map();
        mergedUsers.forEach((athlete) => {
          const athleteId = String(athlete?.user_id || "").trim();
          if (!athleteId) return;
          const emails = [
            normalizeEmail(athlete?.teamsnap_email),
            normalizeEmail(athlete?.users?.email),
            normalizeEmail(athlete?.email)
          ].filter(Boolean);
          if (emails.length === 0) return;
          rosteredEmailsByAthleteId.set(athleteId, emails);
          emails.forEach((email) => {
            if (!rosteredAthleteIdByEmail.has(email)) {
              rosteredAthleteIdByEmail.set(email, athleteId);
            }
          });
        });
        const rosteredEmails = Array.from(new Set(
          Array.from(rosteredEmailsByAthleteId.values()).flat()
        ));
        const rosteredEmailSet = new Set(rosteredEmails);
        const athleteIds = mergedUsers.map((athlete) => athlete.user_id).filter(Boolean);
        const usersWithScores = mergedUsers.map((athlete) => ({
          ...athlete,
          email: athlete.users?.email || athlete.teamsnap_email || "",
          skillAvg: "0.0",
          combineAvg: "0.0",
          overall: "0.0",
          user_skill_scores: [],
          user_combine_scores: [],
          matchedDraft: null,
          hasPublishedSwot: false
        }));

        const formattedPendingPlayers = (pendingPlayerInvites || []).map((invite) => ({
          user_id: invite.id,
          users: { full_name: invite.email },
          email: invite.email,
          type: "pending",
          status: "Pending",
          skillAvg: "0.0",
          combineAvg: "0.0",
          overall: 0,
          user_skill_scores: [],
          user_combine_scores: []
        }));

        if (cancelled) return;
        setAthletes([...formattedPendingPlayers, ...usersWithScores]);
        setDraftEvaluations([]);
        setSwotStatusByAthlete({});

        (async () => {
          try {
            if (cancelled) return;
            const [draftSwotsRes, publishedSwotsRes, swotRowsRes, draftEmailSwotsRes, allSkillScoresRes, allCombineScoresRes] = await Promise.all([
              supabase
                .from("athlete_swot")
                .select("id, prebuilt_name, prebuilt_email, status, created_at, strengths, weaknesses, opportunities, threats, player_type")
                .eq("program_id", activeProgramId)
                .in("status", ["draft", "published"])
                .is("athlete_id", null)
                .is("position_id", null)
                .is("secondary_position_id", null)
                .order("created_at", { ascending: false }),
              rosteredAthleteIds.length > 0
                ? supabase
                  .from("athlete_swot")
                  .select("athlete_id")
                  .eq("program_id", activeProgramId)
                  .eq("status", "published")
                  .in("athlete_id", rosteredAthleteIds)
                : Promise.resolve({ data: [], error: null }),
              rosteredAthleteIds.length > 0
                ? supabase
                  .from("athlete_swot")
                  .select("athlete_id, status, strengths, weaknesses, opportunities, threats, updated_at")
                  .eq("program_id", activeProgramId)
                  .in("athlete_id", rosteredAthleteIds)
                  .order("updated_at", { ascending: false })
                : Promise.resolve({ data: [], error: null }),
              rosteredEmails.length > 0
                ? supabase
                  .from("athlete_swot")
                  .select("prebuilt_email, status, strengths, weaknesses, opportunities, threats, updated_at")
                  .eq("program_id", activeProgramId)
                  .is("athlete_id", null)
                  .in("status", ["draft", "published"])
                  .order("updated_at", { ascending: false })
                : Promise.resolve({ data: [], error: null }),
              athleteIds.length
                ? supabase
                  .from("user_skill_scores")
                  .select("athlete_id, user_score, program_goal_skill_scores!fk_user_skill_scores_program_goal_skill_score(skill_name, target_score)")
                  .in("athlete_id", athleteIds)
                  .eq("program_id", activeProgramId)
                : Promise.resolve({ data: [], error: null }),
              athleteIds.length
                ? supabase
                  .from("user_combine_score")
                  .select("athlete_id, user_score, program_combine_scores(drill_name, target_score)")
                  .in("athlete_id", athleteIds)
                  .eq("program_id", activeProgramId)
                : Promise.resolve({ data: [], error: null })
            ]);

            if (draftSwotsRes.error) throw draftSwotsRes.error;
            if (publishedSwotsRes.error) throw publishedSwotsRes.error;
            if (swotRowsRes.error) throw swotRowsRes.error;
            if (draftEmailSwotsRes.error) throw draftEmailSwotsRes.error;
            if (allSkillScoresRes.error) throw allSkillScoresRes.error;
            if (allCombineScoresRes.error) throw allCombineScoresRes.error;

            // debug logs removed

            const nextDrafts = draftSwotsRes.data || [];
            const draftByEmailNext = new Map();
            nextDrafts.forEach((draft) => {
              const key = normalizeEmail(draft.prebuilt_email);
              const existing = draftByEmailNext.get(key);
              if (!key) return;
              const getDraftPriority = (row) => ({
                published: row?.status === "published" ? 1 : 0,
                seeded: isBlankDraft(row) ? 0 : 1,
                createdAt: new Date(row?.created_at || 0).getTime()
              });
              const currentPriority = existing ? getDraftPriority(existing) : null;
              const nextPriority = getDraftPriority(draft);
              const shouldReplace = !existing ||
                nextPriority.published > currentPriority.published ||
                (nextPriority.published === currentPriority.published && nextPriority.seeded > currentPriority.seeded) ||
                (
                  nextPriority.published === currentPriority.published &&
                  nextPriority.seeded === currentPriority.seeded &&
                  nextPriority.createdAt > currentPriority.createdAt
                );
              if (shouldReplace) {
                draftByEmailNext.set(key, draft);
              }
            });

            const publishedSwotIds = new Set((publishedSwotsRes.data || []).map((row) => String(row.athlete_id)));
            const nextStatus = {};
            const emptyList = (value) => !Array.isArray(value) || value.length === 0;
            (swotRowsRes.data || []).forEach((row) => {
              const athleteKey = String(row.athlete_id);
              if (nextStatus[athleteKey]) return;
              const isBlank = emptyList(row.strengths) &&
                emptyList(row.weaknesses) &&
                emptyList(row.opportunities) &&
                emptyList(row.threats);
              nextStatus[athleteKey] = { hasSwot: true, isBlank, status: row.status || "draft" };
            });

            const draftEmailRows = (draftEmailSwotsRes.data || []).filter((row) => {
              const email = normalizeEmail(row.prebuilt_email);
              return email && rosteredEmailSet.has(email);
            });
            const latestDraftByEmail = new Map();
            draftEmailRows.forEach((row) => {
              const email = normalizeEmail(row.prebuilt_email);
              if (!email) return;
              const updatedAt = new Date(row.updated_at || 0).getTime();
              const existing = latestDraftByEmail.get(email);
              const existingTime = existing ? new Date(existing.updated_at || 0).getTime() : 0;
              if (!existing || updatedAt >= existingTime) {
                latestDraftByEmail.set(email, row);
              }
            });
            const latestDraftByAthlete = new Map();
            draftEmailRows.forEach((row) => {
              const email = normalizeEmail(row.prebuilt_email);
              if (!email) return;
              const athleteId = rosteredAthleteIdByEmail.get(email);
              if (!athleteId) return;
              const updatedAt = new Date(row.updated_at || 0).getTime();
              const existing = latestDraftByAthlete.get(athleteId);
              const existingTime = existing ? new Date(existing.updated_at || 0).getTime() : 0;
              if (!existing || updatedAt >= existingTime) {
                latestDraftByAthlete.set(athleteId, row);
              }
            });
            latestDraftByAthlete.forEach((row, athleteId) => {
              const athleteKey = String(athleteId || "");
              if (!athleteKey || nextStatus[athleteKey]) return;
              const isBlank = emptyList(row.strengths) &&
                emptyList(row.weaknesses) &&
                emptyList(row.opportunities) &&
                emptyList(row.threats);
              nextStatus[athleteKey] = { hasSwot: true, isBlank, status: row.status || "draft" };
            });

            const skillByAthlete = new Map();
            (allSkillScoresRes.data || []).forEach((row) => {
              const key = String(row.athlete_id);
              if (!skillByAthlete.has(key)) skillByAthlete.set(key, []);
              skillByAthlete.get(key).push(row);
            });

            const combineByAthlete = new Map();
            (allCombineScoresRes.data || []).forEach((row) => {
              const key = String(row.athlete_id);
              if (!combineByAthlete.has(key)) combineByAthlete.set(key, []);
              combineByAthlete.get(key).push(row);
            });

            const enrichedUsers = mergedUsers.map((athlete) => {
              const athleteId = String(athlete.user_id);
              const skillScores = skillByAthlete.get(athleteId) || [];
              const combineScores = combineByAthlete.get(athleteId) || [];
              const skillPercents = skillScores.map((s) => clampReadinessPercent(s.user_score, s.program_goal_skill_scores?.target_score));
              const combinePercents = combineScores.map((s) => clampReadinessPercent(s.user_score, s.program_combine_scores?.target_score));
              const skillAvg = skillPercents.length > 0 ? skillPercents.reduce((a, b) => a + b, 0) / skillPercents.length : 0;
              const combineAvg = combinePercents.length > 0 ? combinePercents.reduce((a, b) => a + b, 0) / combinePercents.length : 0;
              const overall = (skillAvg + combineAvg) / 2;
              return {
                ...athlete,
                email: athlete.users?.email || athlete.teamsnap_email || "",
                skillAvg: skillAvg.toFixed(1),
                combineAvg: combineAvg.toFixed(1),
                overall: overall.toFixed(1),
                user_skill_scores: skillScores,
                user_combine_scores: combineScores,
                matchedDraft: (() => {
                  const emails = [
                    normalizeEmail(athlete?.teamsnap_email),
                    normalizeEmail(athlete?.users?.email),
                    normalizeEmail(athlete?.email)
                  ].filter(Boolean);
                  return emails.map((email) => latestDraftByEmail.get(email)).find(Boolean)
                    || draftByEmailNext.get(normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email))
                    || null;
                })(),
                hasPublishedSwot: publishedSwotIds.has(String(athlete.user_id))
              };
            });

            if (cancelled) return;
            setAthletes([...formattedPendingPlayers, ...enrichedUsers]);
            setDraftEvaluations(Array.from(draftByEmailNext.values()));
            if (rosteredAthleteIds.length > 0) {
              const statusPayload = {};
              rosteredAthleteIds.forEach((id) => {
                const key = String(id);
                statusPayload[key] = nextStatus[key] || { hasSwot: false, isBlank: true };
              });
              setSwotStatusByAthlete(statusPayload);
            } else {
              setSwotStatusByAthlete({});
            }
          } catch (err) {
            if (!cancelled) console.error("Error enriching roster data:", err);
          }
        })();
      } catch (err) {
        if (!cancelled) console.error("Error fetching data:", err);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedProgramId, selectedTeamId, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    const fetchAthletePlayerTypes = async () => {
      if (!selectedProgramId) {
        setPlayerTypeByAthlete({});
        return;
      }
      const rosteredAthletes = athletes.filter((athlete) => athlete.type !== "pending");
      const rosteredIds = rosteredAthletes
        .map((athlete) => athlete.user_id)
        .filter(Boolean);
      const rosteredEmailsByAthleteId = new Map();
      rosteredAthletes.forEach((athlete) => {
        const athleteId = String(athlete?.user_id || "").trim();
        if (!athleteId) return;
        const emails = [
          normalizeEmail(athlete?.teamsnap_email),
          normalizeEmail(athlete?.users?.email),
          normalizeEmail(athlete?.email)
        ].filter(Boolean);
        if (emails.length === 0) return;
        rosteredEmailsByAthleteId.set(athleteId, emails);
      });
      const rosteredEmails = Array.from(new Set(
        Array.from(rosteredEmailsByAthleteId.values()).flat()
      ));
      const rosteredEmailSet = new Set(rosteredEmails);
      if (rosteredIds.length === 0) {
        setPlayerTypeByAthlete({});
        return;
      }
      try {
        const [rosteredSwotsRes, draftSwotsRes] = await Promise.all([
          supabase
            .from("athlete_swot")
            .select("athlete_id, position_id, secondary_position_id, player_type, updated_at")
            .eq("program_id", selectedProgramId)
            .in("athlete_id", rosteredIds),
          rosteredEmails.length > 0
            ? supabase
              .from("athlete_swot")
              .select("prebuilt_email, position_id, secondary_position_id, player_type, updated_at")
              .eq("program_id", selectedProgramId)
              .is("athlete_id", null)
            : Promise.resolve({ data: [], error: null })
        ]);
        if (rosteredSwotsRes.error) throw rosteredSwotsRes.error;
        if (draftSwotsRes.error) throw draftSwotsRes.error;
        if (cancelled) return;
        const next = {};
        const latestPrimary = new Map();
        const latestFallback = new Map();
        (rosteredSwotsRes.data || []).forEach((row) => {
          if (!row?.player_type) return;
          const athleteKey = String(row.athlete_id || "").trim();
          if (!athleteKey) return;
          const updatedAt = new Date(row.updated_at || 0).getTime();
          const isPrimary = Boolean(row.position_id) && !row.secondary_position_id;
          const targetMap = isPrimary ? latestPrimary : latestFallback;
          const existing = targetMap.get(athleteKey);
          const existingTime = existing ? existing.updatedAt : 0;
          if (!existing || updatedAt >= existingTime) {
            targetMap.set(athleteKey, { player_type: row.player_type, updatedAt });
          }
        });

        const latestDraftByEmail = new Map();
        (draftSwotsRes.data || []).forEach((row) => {
          if (!row?.player_type) return;
          const email = normalizeEmail(row.prebuilt_email);
          if (!email || !rosteredEmailSet.has(email)) return;
          const updatedAt = new Date(row.updated_at || 0).getTime();
          const existing = latestDraftByEmail.get(email);
          const existingTime = existing ? existing.updatedAt : 0;
          if (!existing || updatedAt >= existingTime) {
            latestDraftByEmail.set(email, { player_type: row.player_type, updatedAt });
          }
        });

        rosteredIds.forEach((id) => {
          const key = String(id);
          const primary = latestPrimary.get(key);
          const fallback = latestFallback.get(key);
          const emails = rosteredEmailsByAthleteId.get(key) || [];
          const draft = emails.find((email) => latestDraftByEmail.has(email));
          const draftPlayerType = draft ? latestDraftByEmail.get(draft) : null;
          const playerTypeId = primary?.player_type || fallback?.player_type || draftPlayerType?.player_type || "";
          if (playerTypeId) {
            next[key] = { player_type: playerTypeId };
          }
        });
        setPlayerTypeByAthlete(next);
        setPlayerTypeDraftByAthlete((prev) => {
          const nextDrafts = { ...prev };
          Object.keys(next).forEach((userId) => {
            if (nextDrafts[userId] === undefined) {
              nextDrafts[userId] = next[userId]?.player_type || "";
            }
          });
          return nextDrafts;
        });
      } catch (err) {
        if (!cancelled) console.error("Error fetching athlete player types:", err);
      }
    };
    fetchAthletePlayerTypes();
    return () => {
      cancelled = true;
    };
  }, [athletes, selectedProgramId]);

  useEffect(() => {
    let cancelled = false;
    const fetchAthletePositions = async () => {
      const nextFallbacks = {};
      const positionLabelByEmail = new Map();
      athletes
        .filter((athlete) => athlete.type !== "pending")
        .forEach((athlete) => {
          const athleteKey = getAthletePlayerTypeKey(athlete);
          if (!athleteKey) return;
          const fallback = abbreviatePositionLabel(athlete.position);
          if (fallback) nextFallbacks[athleteKey] = fallback;
          const athleteEmail = normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email);
          if (athleteEmail && fallback) {
            positionLabelByEmail.set(athleteEmail, fallback);
          }
        });

      const rosteredIds = athletes
        .filter((athlete) => athlete.type !== "pending")
        .map((athlete) => athlete.user_id)
        .filter(Boolean);
      const draftEmails = Array.from(new Set([
        ...athletes
          .filter((athlete) => athlete.type !== "pending")
          .map((athlete) => normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email))
          .filter(Boolean),
        ...draftEvaluations
          .map((draft) => normalizeEmail(draft.prebuilt_email))
          .filter(Boolean)
      ]));

      if (!programSportId && draftEmails.length === 0) {
        setAthletePositionsByAthlete(nextFallbacks);
        setAthletePositionIdsByAthlete({});
        return;
      }

      try {
        let draftPositionRows = [];
        if (selectedProgramId && draftEmails.length > 0) {
          const { data, error: draftRowsError } = await supabase
            .from("athlete_swot")
            .select("prebuilt_email, position_id, secondary_position_id, player_type, updated_at")
            .eq("program_id", selectedProgramId)
            .is("athlete_id", null)
            .order("updated_at", { ascending: true });
          if (draftRowsError) throw draftRowsError;
          draftPositionRows = (data || []).filter((row) =>
            draftEmails.includes(normalizeEmail(row.prebuilt_email))
          );
        }

        let rosteredSwotRows = [];
        if (selectedProgramId && rosteredIds.length > 0) {
          const { data, error: swotError } = await supabase
            .from("athlete_swot")
            .select("athlete_id, position_id, secondary_position_id, player_type, updated_at")
            .eq("program_id", selectedProgramId)
            .in("athlete_id", rosteredIds);
          if (swotError) throw swotError;
          rosteredSwotRows = data || [];
        }

        const positionIds = Array.from(new Set([
          ...(draftPositionRows || []).flatMap((row) => [row.position_id, row.secondary_position_id]),
          ...(rosteredSwotRows || []).flatMap((row) => [row.position_id, row.secondary_position_id])
        ]
          .map((value) => String(value || "").trim())
          .filter(Boolean)
        ));

        const positionById = new Map();
        if (positionIds.length > 0) {
          const { data: positionsData, error: positionsError } = await supabase
            .from("positions")
            .select("id, name, abbreviation")
            .in("id", positionIds);
          if (positionsError) throw positionsError;
          (positionsData || []).forEach((row) => {
            positionById.set(String(row.id), row);
          });
        }

        const next = { ...nextFallbacks };
        const positionIdsByAthlete = {};
        const secondaryTypesByAthlete = {};
        const latestPrimaryByAthlete = new Map();
        const latestSecondaryByAthlete = new Map();
        const latestPlayerTypeByAthletePosition = new Map();

        (rosteredSwotRows || []).forEach((row) => {
          const athleteKey = String(row.athlete_id || "").trim();
          if (!athleteKey) return;
          const updatedAt = new Date(row.updated_at || 0).getTime();
          const playerTypeId = String(row.player_type || "").trim();

          if (row.position_id) {
            const existing = latestPrimaryByAthlete.get(athleteKey);
            const existingTime = existing ? new Date(existing.updated_at || 0).getTime() : 0;
            if (!existing || updatedAt >= existingTime) {
              latestPrimaryByAthlete.set(athleteKey, row);
            }
            if (playerTypeId) {
              const posId = String(row.position_id || "").trim();
              if (posId) {
                const existingType = latestPlayerTypeByAthletePosition.get(athleteKey) || {};
                const current = existingType[posId];
                if (!current || updatedAt >= current.updatedAt) {
                  latestPlayerTypeByAthletePosition.set(athleteKey, {
                    ...existingType,
                    [posId]: { player_type: playerTypeId, updatedAt }
                  });
                }
              }
            }
          }

          if (row.secondary_position_id) {
            const existingSecondary = latestSecondaryByAthlete.get(athleteKey);
            const existingSecondaryTime = existingSecondary ? new Date(existingSecondary.updated_at || 0).getTime() : 0;
            if (!existingSecondary || updatedAt >= existingSecondaryTime) {
              latestSecondaryByAthlete.set(athleteKey, row);
            }
            if (playerTypeId) {
              const posId = String(row.secondary_position_id || "").trim();
              if (posId) {
                const existingType = latestPlayerTypeByAthletePosition.get(athleteKey) || {};
                const current = existingType[posId];
                if (!current || updatedAt >= current.updatedAt) {
                  latestPlayerTypeByAthletePosition.set(athleteKey, {
                    ...existingType,
                    [posId]: { player_type: playerTypeId, updatedAt }
                  });
                }
              }
            }
          }
        });

        rosteredIds.forEach((id) => {
          const athleteKey = String(id);
          const primaryRow = latestPrimaryByAthlete.get(athleteKey);
          const secondaryRow = latestSecondaryByAthlete.get(athleteKey);
          const ids = [
            primaryRow?.position_id,
            secondaryRow?.secondary_position_id
          ]
            .map((value) => String(value || "").trim())
            .filter(Boolean);
          if (ids.length === 0) return;
          positionIdsByAthlete[athleteKey] = ids;
          const labels = ids
            .map((posId) => positionById.get(String(posId)))
            .filter(Boolean)
            .map((position) => abbreviatePositionLabel(position.abbreviation || position.name));
          if (labels.length > 0) {
            next[athleteKey] = labels.join("/");
          }
          if (secondaryRow?.secondary_position_id) {
            secondaryTypesByAthlete[athleteKey] = {
              ...(secondaryTypesByAthlete[athleteKey] || {}),
              [String(secondaryRow.secondary_position_id)]: String(secondaryRow.player_type || "")
            };
          }
        });

        const draftPositionIdsByEmail = new Map();
        const secondaryTypesByEmail = new Map();
        const latestPlayerTypeByEmailPosition = new Map();
        const latestPrimaryByEmail = new Map();
        const latestSecondaryByEmail = new Map();

        (draftPositionRows || []).forEach((row) => {
          const draftEmail = normalizeEmail(row.prebuilt_email);
          if (!draftEmail) return;
          const updatedAt = new Date(row.updated_at || 0).getTime();

          if (row.position_id) {
            const existing = latestPrimaryByEmail.get(draftEmail);
            const existingTime = existing ? new Date(existing.updated_at || 0).getTime() : 0;
            if (!existing || updatedAt >= existingTime) {
              latestPrimaryByEmail.set(draftEmail, row);
            }
            const playerTypeId = String(row.player_type || "").trim();
            const posId = String(row.position_id || "").trim();
            if (playerTypeId && posId) {
              const existingTypes = latestPlayerTypeByEmailPosition.get(draftEmail) || {};
              const current = existingTypes[posId];
              if (!current || updatedAt >= current.updatedAt) {
                latestPlayerTypeByEmailPosition.set(draftEmail, {
                  ...existingTypes,
                  [posId]: { player_type: playerTypeId, updatedAt }
                });
              }
            }
          }

          if (row.secondary_position_id) {
            const existingSecondary = latestSecondaryByEmail.get(draftEmail);
            const existingSecondaryTime = existingSecondary ? new Date(existingSecondary.updated_at || 0).getTime() : 0;
            if (!existingSecondary || updatedAt >= existingSecondaryTime) {
              latestSecondaryByEmail.set(draftEmail, row);
            }
            const playerTypeId = String(row.player_type || "").trim();
            const posId = String(row.secondary_position_id || "").trim();
            if (playerTypeId && posId) {
              const existingTypes = latestPlayerTypeByEmailPosition.get(draftEmail) || {};
              const current = existingTypes[posId];
              if (!current || updatedAt >= current.updatedAt) {
                latestPlayerTypeByEmailPosition.set(draftEmail, {
                  ...existingTypes,
                  [posId]: { player_type: playerTypeId, updatedAt }
                });
              }
            }
          }
        });

        latestPrimaryByEmail.forEach((row, email) => {
          const ids = draftPositionIdsByEmail.get(email) || [];
          const posId = String(row.position_id || "").trim();
          if (posId && !ids.includes(posId)) ids.push(posId);
          draftPositionIdsByEmail.set(email, ids);
        });

        latestSecondaryByEmail.forEach((row, email) => {
          const ids = draftPositionIdsByEmail.get(email) || [];
          const posId = String(row.secondary_position_id || "").trim();
          if (posId && !ids.includes(posId)) ids.push(posId);
          draftPositionIdsByEmail.set(email, ids);
          if (posId) {
            const existing = secondaryTypesByEmail.get(email) || {};
            secondaryTypesByEmail.set(email, { ...existing, [posId]: String(row.player_type || "") });
          }
        });

        draftPositionIdsByEmail.forEach((ids, email) => {
          const labels = ids
            .map((id) => positionById.get(String(id)))
            .filter(Boolean)
            .map((position) => abbreviatePositionLabel(position.abbreviation || position.name));
          if (labels.length > 0) {
            positionLabelByEmail.set(email, labels.join("/"));
          }
        });

        const mergedSecondaryTypes = { ...secondaryTypesByAthlete };
        latestPlayerTypeByAthletePosition.forEach((positions, athleteKey) => {
          Object.keys(positions || {}).forEach((posId) => {
            const playerTypeId = positions[posId]?.player_type;
            if (!playerTypeId) return;
            mergedSecondaryTypes[athleteKey] = {
              ...(mergedSecondaryTypes[athleteKey] || {}),
              [String(posId)]: String(playerTypeId)
            };
          });
        });

        athletes
          .filter((athlete) => athlete.type !== "pending")
          .forEach((athlete) => {
            const athleteEmail = normalizeEmail(athlete.users?.email || athlete.teamsnap_email || athlete.email);
            const athleteKey = getAthletePlayerTypeKey(athlete);
            const label = next[athleteKey] || (athleteEmail ? positionLabelByEmail.get(athleteEmail) : "");
            if (athleteKey && label) {
              next[athleteKey] = label;
            }
            if (athleteEmail && label) {
              positionLabelByEmail.set(athleteEmail, label);
            }
            if (athleteKey && athleteEmail && !positionIdsByAthlete[athleteKey]) {
              const draftIds = draftPositionIdsByEmail.get(athleteEmail) || [];
              if (draftIds.length > 0) {
                positionIdsByAthlete[athleteKey] = draftIds;
              }
            }
            if (athleteKey && athleteEmail) {
              const byEmail = secondaryTypesByEmail.get(athleteEmail);
              if (byEmail) {
                mergedSecondaryTypes[athleteKey] = {
                  ...(mergedSecondaryTypes[athleteKey] || {}),
                  ...byEmail
                };
              }
              const byEmailPosition = latestPlayerTypeByEmailPosition.get(athleteEmail);
              if (byEmailPosition) {
                Object.keys(byEmailPosition).forEach((posId) => {
                  const playerTypeId = byEmailPosition[posId]?.player_type;
                  if (!playerTypeId) return;
                  mergedSecondaryTypes[athleteKey] = {
                    ...(mergedSecondaryTypes[athleteKey] || {}),
                    [String(posId)]: String(playerTypeId)
                  };
                });
              }
            }
          });

        draftEvaluations.forEach((draft) => {
          const draftKey = getAthletePlayerTypeKey(draft);
          const draftEmail = normalizeEmail(draft.prebuilt_email);
          if (!draftKey || !draftEmail) return;
          const matchedLabel = positionLabelByEmail.get(draftEmail);
          if (matchedLabel) {
            next[draftKey] = matchedLabel;
          }
          const byEmail = secondaryTypesByEmail.get(draftEmail);
          if (byEmail) {
            mergedSecondaryTypes[draftKey] = {
              ...(mergedSecondaryTypes[draftKey] || {}),
              ...byEmail
            };
          }
        });

        if (cancelled) return;
        setAthletePositionsByAthlete(next);
        setAthletePositionIdsByAthlete(positionIdsByAthlete);
        setSecondaryPlayerTypeByAthletePosition(mergedSecondaryTypes);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching athlete positions:", err);
          setAthletePositionsByAthlete(nextFallbacks);
          setAthletePositionIdsByAthlete({});
          setSecondaryPlayerTypeByAthletePosition({});
        }
      }
    };

    fetchAthletePositions();
    return () => {
      cancelled = true;
    };
  }, [athletes, draftEvaluations, programSportId, selectedProgramId]);

  return {
    athletes,
    setAthletes,
    draftEvaluations,
    setDraftEvaluations,
    coaches,
    setCoaches,
    programSportId,
    playerTypeOptions,
    playerTypeByAthlete,
    setPlayerTypeByAthlete,
    secondaryPlayerTypeByAthletePosition,
    athletePositionsByAthlete,
    athletePositionIdsByAthlete,
    positionOptions,
    playerTypeDraftByAthlete,
    setPlayerTypeDraftByAthlete,
    swotStatusByAthlete,
    setSwotStatusByAthlete
  };
};
